import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Pagination, Button, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';
import { getApi, getLiveForexRates } from '../../api/api';
import PageTitle from '../layouts/PageTitle';
import Loading from '../components/Loading';
import TradeDetailsModal from '../components/TradeDetailsModal';

const ForexTrades = ({ auth }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [trades, setTrades] = useState([]);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [status, setStatus] = useState('active');
    const [sortBy, setSortBy] = useState('createdAt');
    const [sortOrder, setSortOrder] = useState(-1);
    const limit = 10;
    const [stats, setStats] = useState(null);
    const [selectedTrade, setSelectedTrade] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [liveRates, setLiveRates] = useState({});
    const [ratesLoading, setRatesLoading] = useState(false);
    const [ratesError, setRatesError] = useState(null);
    const [livePnL, setLivePnL] = useState({});
    const [isRefreshing, setIsRefreshing] = useState(false);

    const fetchTrades = async (page) => {
        try {
            setLoading(true);
            setError(null);
            const response = await getApi(
                `trades/forex?page=${page}&limit=${limit}&status=${status}&sortBy=openDate&sortOrder=-1`
            );
            
            if (response.success) {
                // Sort trades by openDate in descending order (latest first)
                const sortedTrades = response.data.trades.sort((a, b) => 
                    new Date(b.openDate) - new Date(a.openDate)
                );
                
                setTrades(sortedTrades);
                setTotalPages(response.data.pagination.pages || 1);
                // setStats(response.data.stats);
            } else {
                throw new Error(response.message || 'Failed to fetch trades');
            }
        } catch (error) {
            console.error('Error fetching forex trades:', error);
            setError(error.message);
            if (error.response?.status === 401) {
                navigate('/login', { replace: true });
            }
        } finally {
            setLoading(false);
        }
    };

    // Calculate live P/L for active trades
    const calculateLivePnL = (trade, currentPrice) => {
        if (!trade || !currentPrice || trade.status !== 'active') return null;
        
        // Calculate P/L based on the wallet adjustment logic
        let profitLoss = 0;
        
        if (trade.direction === 'buy') {
            // For buy trades: (currentPrice - entryPrice) * quantity * 100000
            profitLoss = (currentPrice - trade.entryPrice) * trade.quantity * 100000;
        } else {
            // For sell trades: (entryPrice - currentPrice) * quantity * 100000
            profitLoss = (trade.entryPrice - currentPrice) * trade.quantity * 100000;
        }
        
        return profitLoss.toFixed(2);
    };

    const fetchLiveRates = async () => {
        try {
            setRatesLoading(true);
            setIsRefreshing(true);
            setRatesError(null);
            
            // Get unique symbols from trades and remove "/" from them
            const uniqueSymbols = [...new Set(trades.map(trade => trade.symbol?.replace('/', '')).filter(Boolean))];
            
            if (uniqueSymbols.length === 0) {
                setRatesLoading(false);
                return;
            }

            const response = await getLiveForexRates(uniqueSymbols);
            
            if (response.quotes) {
                const ratesMap = {};
                response.quotes.forEach(quote => {
                    const symbol = `${quote.base_currency}${quote.quote_currency}`;
                    ratesMap[symbol] = quote;
                });
                
                setLiveRates(ratesMap);

                // Update live P/L for active trades
                setLivePnL(prev => {
                    const newPnL = { ...prev };
                    trades.forEach(trade => {
                        const cleanSymbol = trade.symbol?.replace('/', '');
                        if (trade.status === 'active' && ratesMap[cleanSymbol]) {
                            const currentPrice = ratesMap[cleanSymbol].mid;
                            newPnL[trade._id] = calculateLivePnL(trade, currentPrice);
                        }
                    });
                    return newPnL;
                });
            }
        } catch (error) {
            console.error('Error fetching live rates:', error);
            setRatesError(error.message);
        } finally {
            setRatesLoading(false);
            setIsRefreshing(false);
        }
    };

    useEffect(() => {
        if (auth.isAuthenticated && auth.token) {
            fetchTrades(currentPage);
        } else {
            navigate('/login', { replace: true });
        }
    }, [auth, currentPage, status, sortBy, sortOrder, navigate]);

    // Fetch live rates when trades change
    useEffect(() => {
        if (trades.length > 0) {
            fetchLiveRates();
        }
    }, [trades]);

    // Set up interval to refresh live rates every 5 seconds
    useEffect(() => {
        if (auth.isAuthenticated && auth.token && trades.length > 0) {
            const interval = setInterval(() => {
                fetchLiveRates();
            }, 5000); // 5 seconds

            return () => clearInterval(interval);
        }
    }, [auth, trades]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handleStatusChange = (newStatus) => {
        setStatus(newStatus);
        setCurrentPage(1);
    };

    const handleSort = (field) => {
        if (sortBy === field) {
            setSortOrder(sortOrder * -1);
        } else {
            setSortBy(field);
            setSortOrder(-1);
        }
        setCurrentPage(1);
    };

    if (loading) return <Loading />;

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                {error}
            </div>
        );
    }

    return (
        <>
            <PageTitle activeMenu="Forex Trades" motherMenu="Trading" />



            {/* TradingView Chart */}
            <div className="row mb-4">
                <div className="col-12">
                    <Card>
                        <Card.Body className="p-0">
                            <div className="tradingview-chart">
                                <iframe
                                    src="https://www.tradingview-widget.com/embed-widget/advanced-chart/?locale=en#%7B%22allow_symbol_change%22%3Atrue%2C%22calendar%22%3Afalse%2C%22details%22%3Afalse%2C%22autosize%22%3Atrue%2C%22width%22%3A%22100%25%22%2C%22height%22%3A%22100%25%22%2C%22hide_side_toolbar%22%3Atrue%2C%22hide_top_toolbar%22%3Afalse%2C%22hide_legend%22%3Afalse%2C%22hide_volume%22%3Afalse%2C%22hotlist%22%3Afalse%2C%22interval%22%3A%22D%22%2C%22save_image%22%3Atrue%2C%22studies%22%3A%5B%5D%2C%22style%22%3A%221%22%2C%22symbol%22%3A%22OANDA%3AEURUSD%22%2C%22theme%22%3A%22light%22%2C%22timezone%22%3A%22Etc%2FUTC%22%2C%22backgroundColor%22%3A%22%23FFFFFF%22%2C%22gridColor%22%3A%22rgba(240%2C%20243%2C%20250%2C%200.06)%22%2C%22watchlist%22%3A%5B%5D%2C%22show_popup_button%22%3Afalse%2C%22popup_width%22%3A%221000%22%2C%22popup_height%22%3A%22650%22%2C%22compareSymbols%22%3A%5B%5D%2C%22support_host%22%3A%22https%3A%2F%2Fwww.tradingview.com%22%2C%22utm_source%22%3A%22www.tradingview.com%22%2C%22utm_medium%22%3A%22widget_new%22%2C%22utm_campaign%22%3A%22advanced-chart%22%2C%22page-uri%22%3A%22www.tradingview.com%2Fwidget-wizard%2Fen%2Flight%2Fadvanced-chart%2F%22%7D"
                                    style={{
                                        width: "100%",
                                        height: "600px",
                                        border: "none"
                                    }}
                                    allowTransparency="true"
                                    scrolling="no"
                                    allowFullScreen
                                    frameBorder="0"
                                ></iframe>
                            </div>
                        </Card.Body>
                    </Card>
                </div>
            </div>

            {/* Error Display for Rates */}
            {ratesError && (
                <div className="alert alert-warning mb-4" role="alert">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    Live rates error: {ratesError}
                </div>
            )}

            {/* Trades Table Card */}
            <Card>
                <Card.Header>
                    <Card.Title>Forex Trades</Card.Title>
                    <div className="d-flex gap-2">
                        <Badge 
                            bg={status === 'active' ? 'primary' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStatusChange('active')}
                        >
                            Active
                        </Badge>
                        <Badge
                            bg={status === 'completed' ? 'primary' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStatusChange('completed')}
                        >
                            Completed
                        </Badge>
                        {/* <Badge 
                            bg={status === 'cancelled' ? 'primary' : 'secondary'}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleStatusChange('cancelled')}
                        >
                            Cancelled
                        </Badge> */}
                    </div>
                </Card.Header>
                <Card.Body>
                    <div className="table-responsive">
                        <Table responsive>
                            <thead>
                                <tr>
                                    <th>Symbol</th>
                                    <th>Direction</th>
                                    <th>Quantity</th>
                                    <th>Entry Price</th>
                                    <th>Current Price</th>
                                    <th>P/L</th>
                                    <th>Status</th>
                                    <th>Date</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trades.map(trade => (
                                    <tr key={trade._id}>
                                        <td>
                                            <Badge bg="primary">
                                                {trade?.symbol || 'N/A'}
                                            </Badge>
                                        </td>
                                        <td>
                                            <Badge bg={trade?.direction === 'buy' ? 'success' : 'danger'}>
                                                {(trade?.direction || 'BUY').toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>{trade?.quantity || 0}</td>
                                        <td>{trade?.entryPrice?.toFixed(5) || '0.00000'}</td>
                                        <td>
                                            {liveRates[trade?.symbol?.replace('/', '')] ? (
                                                <div className={isRefreshing ? 'refreshing-rates' : ''}>
                                                    <div className="fw-bold">
                                                        {liveRates[trade.symbol.replace('/', '')].mid?.toFixed(5)}
                                                    </div>
                                                    <div className="small text-muted">
                                                        B: {liveRates[trade.symbol.replace('/', '')].bid?.toFixed(5)} | 
                                                        A: {liveRates[trade.symbol.replace('/', '')].ask?.toFixed(5)}
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="text-muted">
                                                    {ratesLoading ? (
                                                        <div className="spinner-border spinner-border-sm" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    ) : (
                                                        'N/A'
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {trade.status === 'active' ? (
                                                <span className={
                                                    isRefreshing ? 'refreshing-pnl' :
                                                    !livePnL[trade._id] ? '' :
                                                    parseFloat(livePnL[trade._id]) >= 0 ? 'text-success' : 'text-danger'
                                                }>
                                                    ${livePnL[trade._id] || '0.00'}
                                                </span>
                                            ) : (
                                                <span className={
                                                    !trade.profitLoss ? '' :
                                                    trade.profitLoss >= 0 ? 'text-success' : 'text-danger'
                                                }>
                                                    ${trade.profitLoss?.toFixed(2) || '0.00'}
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <Badge bg={
                                                !trade?.status ? 'secondary' :
                                                trade.status === 'active' ? 'warning' : 
                                                trade.status === 'completed' ? 'success' :
                                                trade.status === 'cancelled' ? 'danger' : 'secondary'
                                            }>
                                                {(trade?.status || 'unknown').toUpperCase()}
                                            </Badge>
                                        </td>
                                        <td>
                                            {trade?.openManualDate + ' ' + trade?.openManualTime}
                                        </td>
                                        <td>
                                            <Button 
                                                variant="light" 
                                                size="sm"
                                                style={{ 
                                                    backgroundColor: '#132720',
                                                    color: '#fff'
                                                }}
                                                onClick={() => {
                                                    setSelectedTrade(trade);
                                                    setShowDetailsModal(true);
                                                }}
                                            >
                                                Details
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    {totalPages > 1 && (
                        <div className="d-flex justify-content-between align-items-center mt-3">
                            <div className="dataTables_info">
                                Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, trades.length)} of {trades.length} entries
                            </div>
                            <div className="dataTables_paginate paging_simple_numbers">
                                <Pagination className="pagination-primary">
                                    <Pagination.First
                                        onClick={() => handlePageChange(1)}
                                        disabled={currentPage === 1}
                                    />
                                    <Pagination.Prev
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    />
                                    
                                    {[...Array(totalPages)].map((_, index) => {
                                        const pageNumber = index + 1;
                                        if (
                                            pageNumber === 1 ||
                                            pageNumber === totalPages ||
                                            (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                                        ) {
                                            return (
                                                <Pagination.Item
                                                    key={pageNumber}
                                                    active={currentPage === pageNumber}
                                                    onClick={() => handlePageChange(pageNumber)}
                                                >
                                                    {pageNumber}
                                                </Pagination.Item>
                                            );
                                        }
                                        if (
                                            pageNumber === currentPage - 2 ||
                                            pageNumber === currentPage + 2
                                        ) {
                                            return <Pagination.Ellipsis key={`ellipsis-${pageNumber}`} />;
                                        }
                                        return null;
                                    })}
                                    
                                    <Pagination.Next
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    />
                                    <Pagination.Last
                                        onClick={() => handlePageChange(totalPages)}
                                        disabled={currentPage === totalPages}
                                    />
                                </Pagination>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <TradeDetailsModal 
                show={showDetailsModal}
                onHide={() => {
                    setShowDetailsModal(false);
                    setSelectedTrade(null);
                }}
                trade={selectedTrade}
            />

            <style jsx>{`
                .tradingview-chart {
                    width: 100%;
                    background: #fff;
                    border-radius: 8px;
                    overflow: hidden;
                }
                @media (max-width: 768px) {
                    .tradingview-chart iframe {
                        height: 400px !important;
                    }
                }
                
                .refreshing-rates {
                    animation: refreshPulse 1s ease-in-out infinite;
                }
                
                .refreshing-pnl {
                    animation: refreshPulse 1s ease-in-out infinite;
                    font-weight: bold;
                }
                
                @keyframes refreshPulse {
                    0% {
                        background-color: rgba(40, 167, 69, 0.1);
                        color: #28a745;
                    }
                    50% {
                        background-color: rgba(220, 53, 69, 0.1);
                        color: #dc3545;
                    }
                    100% {
                        background-color: rgba(40, 167, 69, 0.1);
                        color: #28a745;
                    }
                }
            `}</style>
        </>
    );
};

const mapStateToProps = (state) => ({
    auth: state.auth
});

export default connect(mapStateToProps)(ForexTrades); 